/* global defaultSearchParameters */

/**
 * Controller for the auto-completes on a page
 *
 * This is the controller that provides the guidance on
 * how AutoCompletes should act on different events and
 * encapsulates the AutoComplete
 */
import Shared from '../../../Templates/Core/JS/Shared.js';
import { AutoComplete } from './AutoComplete.js';
import Parameters from '../../../Templates/Core/JS/Parameters.js';
import { UrlHandler } from '../../../Templates/Core/JS/Search/UrlHandler.js';

export class AutoCompleteController {
	/**
	 * Default constructor for the Controller
	 */

	constructor(searchRequest = null) {
		this.controlKeys = {
			down: 40,
			up: 38,
			enter: 13,
			esc: 27,
			tab: 9,
			ctrl: 17
		};

		this.searchRequest = searchRequest;

		this.autoCompleteForms = {};

		const inputs = Array.from(document.querySelectorAll('input.AutoCompleteInput, input.js-autoCompleteInput'));

		// Bail out if there are no inputs to work with
		if (inputs.length === 0) {
			return;
		}

		// Initialise each input box as an autocomplete

		Array.from(inputs).forEach((input) => {
			this._initAutoCompleteInstance(input);
		});

		// Add the click events for the submit buttons for each form

		const buttons = [];
		Object.values(this.autoCompleteForms).forEach((autocompletes) => {
			buttons.push(autocompletes[0].submitButton);
		});

		Array.from(buttons).forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();
				const formID = event.target.closest('form').id;
				this._handleButtonClick(formID);
			});
		});
	}

	/**
	 * Submit the current form using its associated AutoCompletes.
	 *
	 * @param {String} formID
	 * @param {boolean} autoCompleteUsed Whether the auto complete was used
	 */

	submit(formID, autoCompleteUsed = false) {
		const form = document.getElementById(formID);
		const button = form.querySelector('button[type="submit"]');
		const input = form.querySelector('.js-autoCompleteInput');
		let formSpecificDefaultSearchParameters = form.dataset.defaultParameters;
		button.classList.add('AnimationPulseLoop');

		const staticParameters = {};

		if (formSpecificDefaultSearchParameters) {
			formSpecificDefaultSearchParameters = JSON.parse(formSpecificDefaultSearchParameters);

			if (input && input.value) {
				// this removes discipline ID's and degree ID's if there is a typed keyword
				delete formSpecificDefaultSearchParameters.discipline_ids;
				delete formSpecificDefaultSearchParameters.degrees;
			}
		}

		if (input && input.dataset.country) {
			staticParameters.country_ids = [input.dataset.country];
		}
		// 'defaultSearchParameters' variable is defined within '../Templates/Controller.tp4'
		const searchParameters = Object.assign(
			staticParameters,
			defaultSearchParameters,
			formSpecificDefaultSearchParameters
		);
		const keywords = {};

		this._fireSubmittedTracking(formID);

		if (!this.autoCompleteForms[formID]) {
			console.error('Invalid autocomplete form ID provided:', formID);
			return;
		}

		document.addEventListener('auto_complete_suggested', () => {
			this._reroute(searchParameters, keywords, button);
		});

		// Go through the autocomplete inputs and update the parameters

		let dispatchEvent = this.autoCompleteForms[formID].length;
		Object.values(this.autoCompleteForms[formID]).forEach((autoComplete) => {
			
			this.assignInputType(autoComplete);

			autoComplete.updateSelectedFilter(() => {
				AutoCompleteController._updateParameters(autoComplete, searchParameters, keywords);

				this._sendTracking(autoComplete, autoCompleteUsed, formID);

				dispatchEvent--;
				if (dispatchEvent === 0) {
					document.dispatchEvent(
						new CustomEvent('auto_complete_suggested', {
							detail: {
								searchParameters: searchParameters,
								keywords: keywords
							}
						})
					);
				}
			});
		});
	}

	assignInputType(autoComplete) {
		if(autoComplete.selectedValue?.name === 'country_ids') {
			autoComplete._inputField.dataset.searchType = 'country';
		}

		if(autoComplete.selectedValue?.name === 'organisation_ids') {
			autoComplete._inputField.dataset.searchType = 'organisation';
		}

		if(autoComplete.selectedValue?.name === 'region_ids') {
			autoComplete._inputField.dataset.searchType = 'region';
		} 

		if(autoComplete.selectedValue?.name === 'discipline_ids') {
			autoComplete._inputField.dataset.searchType = 'discipline';
		}

		if(autoComplete.selectedValue?.name === 'specialisation_ids') {
			autoComplete._inputField.dataset.searchType = 'discipline';
		}
	}

	/**
	 * Hide all the autoCompletes on the page
	 */

	hide() {
		this._autoCompletes.forEach((autoComplete) => {
			autoComplete.classList.add('Hidden');
		});
	}

	/**
	 * Fire tracking on submitting the search form
	 *
	 * @private
	 */

	_fireSubmittedTracking(formID) {
		const category = 'Search';
		const action = `Submit form: ${formID}`;

		Shared.addFunctionToPipelineTracker((dataTracker) => {
			dataTracker.trackStructEvent(category, action);
		});
	}

	/**
	 * Get an array of all the auto-completes on the page
	 *
	 * This disregards the form that the auto-completes belong to
	 *
	 * @returns {Array}
	 */

	get _autoCompletes() {
		const autoCompletes = [];

		Object.values(this.autoCompleteForms).forEach((autoCompleteForm) => {
			autoCompleteForm.forEach((autoComplete) => {
				autoCompletes.push(autoComplete);
			});
		});

		return autoCompletes;
	}

	/**
	 * Initialise an autocomplete instance for a given input field
	 *
	 * @param {Element} input
	 * @private
	 */

	_initAutoCompleteInstance(input) {
		// Make sure the form specific list is there

		const formID = input.closest('form').id;

		// Make sure the form has an ID

		if (!formID) {
			console.error(
				'AutoCompleteController.js:',
				'Autocomplete form does not have an ID.',
				'Enhanced functionality will be disabled.'
			);

			return;
		}

		if (!this.autoCompleteForms[formID]) {
			this.autoCompleteForms[formID] = [];
		}

		const index = this.autoCompleteForms[formID].length;

		// Create and add the controller

		const autoComplete = new AutoComplete(input, index, this.searchRequest);
		this.autoCompleteForms[formID].push(autoComplete);

		this._attachEventListenersTo(autoComplete);

		// Hide the auto-complete as an extra safety mechanism

		autoComplete.hide();
	}

	/**
	 * Based on the given parameters, reroute to the correct search URL
	 *
	 * Stores the search terms we have used in local storage
	 * so that they can be displayed on the search page again
	 *
	 * @abstract
	 * @protected
	 */

	_reroute() {
		// eslint-disable-next-line no-console
		console.warning('_reroute method should be implemented in subclass');
	}

	/**
	 * Update the parameters and keywords that we have
	 *
	 * @param {AutoComplete} autoComplete
	 * @param {Object} searchParameters
	 * @param {Object} keywords
	 * @private
	 * @static
	 */

	static _updateParameters(autoComplete, searchParameters, keywords) {
		const inputValues = autoComplete.inputValues;
		const inputType = autoComplete.suggestType;

		if (!keywords[inputType]) {
			keywords[inputType] = inputValues.keyword || inputValues.title;
		}

		if (inputValues.keyword !== '') {
			const keyword = inputValues.keyword;

			if (typeof searchParameters['keywords'] === 'undefined') {
				searchParameters['keywords'] = keyword;
			} else {
				searchParameters['keywords'] += ` ${keyword}`;
			}

			return;
		}

		if (inputValues.id !== null) {
			searchParameters[inputValues.name] = [inputValues.id];
		}
	}

	/**
	 * Adds event listeners to the specified autoComplete.
	 *
	 * @param autoComplete
	 */

	_attachEventListenersTo(autoComplete) {
		const inputField = autoComplete._inputField;

		const debouncedKeyUp = Shared.debounce((event) => {
			this._handleKeyUp(autoComplete, event);
		}, 500);

		inputField.addEventListener('keyup', (event) => {
			debouncedKeyUp(event);
		});

		inputField.addEventListener('focus', () => {
			// Only suggest on focus when we are on desktop
			// On mobile this would interfere with the expected behaviour

			Shared.breakpoints(['Large', 'ExtraLarge'], () => {
				autoComplete.show();
			});
		});

		inputField.addEventListener('click', () => {
			// Only suggest on click for mobile devices

			Shared.breakpoints(['Small', 'Medium'], () => {
				autoComplete.show();
			});
		});

		inputField.addEventListener('keydown', (event) => {
			this._handleKeyDown(autoComplete, event);
		});

		autoComplete.onSelectionChange(() => {
			this._handleSelectionChange(autoComplete);
		});

		inputField.addEventListener('blur', () => {
			autoComplete.hide();
		});

		document.addEventListener('render_complete', () => {
			autoComplete.submitButton.classList.remove('AnimationPulseLoop');
		});

		// Initialise an optimised resize event

		Shared.throttle('resize', window, () => {
			autoComplete.setAutoCompleteWidth();
		});

		window.addEventListener('orientationchange', () => {
			autoComplete.setAutoCompleteWidth();
		});
	}

	/**
	 * Handle for when a key is released. Mainly for all regular characters.
	 *
	 * @param autoComplete
	 * @param event
	 */

	_handleKeyUp(autoComplete, event) {
		let isControlKey = false;
		for (const key in this.controlKeys) {
			if (Object.prototype.hasOwnProperty.call(this.controlKeys, key) && this.controlKeys[key] === event.keyCode) {
				isControlKey = true;
				break;
			}
		}

		if (isControlKey === false) {
			autoComplete.suggest();
		}
	}

	/**
	 * Handle for when a key is pressed down. Mainly for special character interaction.
	 *
	 * @param autoComplete
	 * @param event
	 */

	_handleKeyDown(autoComplete, event) {
		if (event.keyCode === this.controlKeys.esc) {
			autoComplete.hide();
		} else if (event.keyCode === this.controlKeys.enter) {
			autoComplete.interaction = 'enter';
			this._handleSelectionChange(autoComplete);
			event.preventDefault();
		} else if (event.keyCode === this.controlKeys.tab) {
			this._handleTabPress(autoComplete, event);
		} else if (event.keyCode === this.controlKeys.up) {
			autoComplete.moveSelectedUp();
			event.preventDefault();
		} else if (event.keyCode === this.controlKeys.down) {
			autoComplete.moveSelectedDown();
			event.preventDefault();
		} else {
			// Not handled key
		}
	}

	/**
	 * Handles for when a 'tab' has been pressed on an AutoComplete's input.
	 *
	 * @param {AutoComplete} autoComplete
	 * @param event
	 * @return {void}
	 */

	_handleTabPress(autoComplete, event) {
		const formID = autoComplete.formID;
		autoComplete.hide();

		if (!this.autoCompleteForms[formID]) {
			return;
		}

		if (event.shift !== true && autoComplete.index !== this.autoCompleteForms[formID].length - 1) {
			const index = autoComplete.index + 1;
			this.autoCompleteForms[formID][index].suggest();
		}
	}

	/**
	 * Handle the click on the submit button in a certain form.
	 *
	 * This function handles the click of a submit button. It will update the
	 * autocomplete buttons in that form and finally submit the form.
	 *
	 * @param {String} formID
	 */

	_handleButtonClick(formID) {
		if (!this.autoCompleteForms[formID]) {
			return;
		}

		this.autoCompleteForms[formID].forEach((autoComplete) => {
			autoComplete.hide();
		});

		this.submit(formID);
	}

	/**
	 * Handle the selection change event.
	 *
	 * Handler for when an AutoComplete has it's selection changed through click
	 * or other sources to which the input is listening.
	 *
	 * @param {AutoComplete} autoComplete
	 */

	_handleSelectionChange(autoComplete) {
		const formID = autoComplete.formID;
		const formElement = document.getElementById(formID);
		const noAutoSubmitAtMobile = formElement.dataset.noAutoSubmit && formElement.dataset.noAutoSubmit === 'true';

		autoComplete.setSelectedValue();
		autoComplete.copySelectionText();
		autoComplete.hide();

		const autoCompleteWasUsed = !!autoComplete.selectedValue;

		if (autoComplete.index !== this.autoCompleteForms[formID].length - 1) {
			const index = autoComplete.index + 1;

			// Request an animation frame to make sure that the focusing only
			// happens once the hide event is done.

			const waitForFrame = window.requestAnimationFrame(() => {
				this.autoCompleteForms[formID][index].focus();
				window.cancelAnimationFrame(waitForFrame);
			});
		}

		if (autoComplete.index === this.autoCompleteForms[formID].length - 1) {
			autoComplete.blur();
			this.submit(formID, autoCompleteWasUsed);
			return;
		}

		if (noAutoSubmitAtMobile) {
			return;
		}
	}

	_sendTracking(autoComplete, autoCompleteUsed, formID) {
		let interaction = null;
		let userTypedKeyword = autoComplete._inputField.value;
		let parameterNameVal = null;
		let autocompleteInteractionPosition = null;

		if (autoCompleteUsed) {
			interaction = autoComplete.interaction;
			userTypedKeyword = autoComplete.originalValue;
			parameterNameVal = autoComplete.matchedValue;
			autocompleteInteractionPosition = autoComplete.selectedPosition;
		}

		// Tracking to see where the search box is used.

		Shared.addFunctionToPipelineTracker((dataTracker) => {
			dataTracker.trackStructEvent('search', `${formID}_box_used`);
		});

		// Tracking to see how the autocomplete is used.

		Shared.addFunctionToPipelineTracker((dataTracker) => {
			dataTracker.trackStructEvent(
				'search',
				interaction,
				userTypedKeyword,
				parameterNameVal,
				autocompleteInteractionPosition
			);
		});
	}
}

export class AutoCompleteControllerSearch extends AutoCompleteController {
	// eslint-disable-line no-unused-vars

	constructor(dataStorage, searchRequest) {
		super(searchRequest);

		this.dataStorage = dataStorage;

		document.addEventListener('url_change', (event) => {
			this.searchRequest = event.detail.searchRequest;
		});
	}

	/**
	 * Based on the given parameters, reroute to the correct search URL
	 *
	 * Stores the search terms we have used in local storage
	 * so that they can be displayed on the search page again
	 *
	 * @param {Object} searchParameters
	 * @param {Object} keywords
	 * @protected
	 */

	_reroute(searchParameters, keywords) {
		this.dataStorage.store('search_terms', keywords);

		if (this.searchRequest !== null) {
			const queryString = Parameters.alias(this.searchRequest.getQParameters());
			if (queryString === '') {
				return;
			}
			this._setRequestParameters(searchParameters);
			UrlHandler.updateSearchUrl(this.searchRequest);
		}
	}

	_setRequestParameters(searchParameters) {
		this.searchRequest.setParameter('tuition_fee_currency', null);
		this.searchRequest.setParameter('organisation_ids', null);
		this.searchRequest.setParameter('discipline_ids', null);
		this.searchRequest.setParameter('country_ids', null);
		this.searchRequest.setParameter('area_ids', null);
		this.searchRequest.setParameter('region_ids', null);
		this.searchRequest.setParameter('start', 0);

		Object.keys(searchParameters).forEach((key) => {
			this.searchRequest.setParameter(key, searchParameters[key]);
		});
	}
}

export class AutoCompleteControllerGeneric extends AutoCompleteController {
	// eslint-disable-line no-unused-vars

	constructor(dataStorage) {
		super();

		this.dataStorage = dataStorage;
	}

	/**
	 * Based on the given parameters, reroute to the correct search URL
	 *
	 * Stores the search terms we have used in local storage
	 * so that they can be displayed on the search page again
	 *
	 * @param {Object} searchParameters
	 * @param {Object} keywords
	 * @protected
	 */

	_reroute(searchParameters, keywords) {
		this.dataStorage.store('search_terms', keywords);

		const parameters = { q: {} };

		Object.keys(searchParameters).forEach((key) => {
			parameters.q[key] = searchParameters[key];
		});

		if (keywords['what']) {
			parameters['kw-what'] = keywords['what'];
		}

		if (keywords['where']) {
			parameters['kw-where'] = keywords['where'];
		}

		const searchUrl = UrlHandler.constructSearchUrl(parameters);
		UrlHandler.setUrl(searchUrl);
	}
}
